"use client";

import { useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from "recharts";
import { getMonthlyStatistics } from "@/services/statistics.service";

interface ChartDataPoint {
  month: string;
  total: number;
  restyle: number;
  cleanRoom: number;
  cameraAngle: number;
  video: number;
}

export function PropertyVisualizeBreakdownChart() {
  const [data, setData] = useState<ChartDataPoint[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchChartData = async () => {
      try {
        const now = new Date();
        const currentYear = now.getFullYear();
        const currentMonth = now.getMonth() + 1;
        const previousMonth = currentMonth === 1 ? 12 : currentMonth - 1;

        const monthNames = [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ];

        let allData: any[] = [];

        if (currentMonth === 1) {
          const [currentYearResponse, previousYearResponse] = await Promise.all(
            [
              getMonthlyStatistics(currentYear),
              getMonthlyStatistics(currentYear - 1),
            ]
          );

          if (currentYearResponse.success && currentYearResponse.data) {
            allData = [...allData, ...currentYearResponse.data];
          }
          if (previousYearResponse.success && previousYearResponse.data) {
            allData = [...allData, ...previousYearResponse.data];
          }
        } else {
          const response = await getMonthlyStatistics(currentYear);
          if (response.success && response.data) {
            allData = response.data;
          }
        }

        if (allData.length > 0) {
          const relevantMonths = [previousMonth, currentMonth];
          const filteredData = allData
            .filter((stat) => {
              const monthNum = parseInt(stat.period.split("-")[1]);
              return relevantMonths.includes(monthNum);
            })
            .map((stat) => ({
              month: monthNames[parseInt(stat.period.split("-")[1]) - 1],
              total: stat.propertyVisualize || 0,
              restyle: stat.propertyVisualizeRestyle || 0,
              cleanRoom: stat.propertyVisualizeCleanRoom || 0,
              cameraAngle: stat.propertyVisualizeCameraAngle || 0,
              video: stat.propertyVisualizeVideo || 0,
            }));

          const chartData = relevantMonths.map((monthNum) => {
            const existing = filteredData.find(
              (d) => d.month === monthNames[monthNum - 1]
            );
            return (
              existing || {
                month: monthNames[monthNum - 1],
                total: 0,
                restyle: 0,
                cleanRoom: 0,
                cameraAngle: 0,
                video: 0,
              }
            );
          });

          setData(chartData);
        }
      } catch (error) {
        console.error(
          "Failed to fetch property visualize breakdown data:",
          error
        );
      } finally {
        setLoading(false);
      }
    };

    fetchChartData();
  }, []);

  if (loading) {
    return (
      <div className="h-[300px] w-full flex items-center justify-center">
        <div className="text-muted-foreground">Loading chart data...</div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="h-[300px] w-full flex items-center justify-center">
        <div className="text-muted-foreground">No data available</div>
      </div>
    );
  }

  return (
    <div className="h-[350px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} barGap={8} barCategoryGap="20%">
          <CartesianGrid strokeDasharray="3 3" opacity={0.2} vertical={false} />
          <XAxis
            dataKey="month"
            tick={{ fontSize: 12, fill: "#6B7280" }}
            axisLine={{ stroke: "#E5E7EB" }}
            tickLine={false}
          />
          <YAxis
            allowDecimals={false}
            tick={{ fontSize: 12, fill: "#6B7280" }}
            axisLine={{ stroke: "#E5E7EB" }}
            tickLine={false}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: "rgba(255, 255, 255, 0.98)",
              borderRadius: "12px",
              border: "1px solid #E5E7EB",
              boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
              padding: "12px",
            }}
            cursor={{ fill: "rgba(157, 78, 221, 0.05)" }}
          />
          <Legend
            wrapperStyle={{ paddingTop: "20px" }}
            iconType="circle"
            iconSize={10}
          />
          <Bar
            dataKey="restyle"
            name="Restyle"
            fill="#8B5CF6"
            radius={[8, 8, 0, 0]}
            maxBarSize={60}
          />
          <Bar
            dataKey="cleanRoom"
            name="Clean Room"
            fill="#06B6D4"
            radius={[8, 8, 0, 0]}
            maxBarSize={60}
          />
          <Bar
            dataKey="cameraAngle"
            name="Camera Angle"
            fill="#F59E0B"
            radius={[8, 8, 0, 0]}
            maxBarSize={60}
          />
          <Bar
            dataKey="video"
            name="Video"
            fill="#10B981"
            radius={[8, 8, 0, 0]}
            maxBarSize={60}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
